import { SOCKET_EVENTS } from '@/api/socket-events';
import { socket } from '@/api/socket';
import { $newBalance, $updateBalance, $updateBalancesByClientId, type IBalance } from '@/entities/balance.entity';
import { $updateCardsByClientId } from '@/entities/card.entity';
import { $updatePhonesByClientId } from '@/entities/phone.entity';
import {
	$addClientDetailBalance,
	$addClientDetailCode,
	$addClientDetailMessage,
	$newClient,
	$selectedClient,
	$setClientDetailBalances,
	$setClientDetailCodes,
	$setClientDetailMessages,
	$setClientDetailActions,
	$addClientDetailAction,
	$updateClientDetailAction,
	$setClientDetailApps,
	$setSelectedClient,
	$updateClient,
	$updateClientDetailBalance,
	type IClient,
	type IClientAction,
	type IClientMessage,
	type IInstalledApp,
} from '@/entities/client.entity';
import { $addMyClient, $selectedMyClient, $setMyClients, $setMyClientsSearchResult, $setSelectedMyClient, $updateMyClient, type IMyClient } from '@/entities/my-clients-entity';
import { $addMyCode, $setMyCodes, type IMyCode } from '@/entities/my-codes-entity';
import { $setOnlineClientsCount } from '@/entities/online-count.entity';
import { $profile, $resetAuth } from '@/entities/profile-entity';
import {
	$appendBuildLogs,
	$setBuildComplete,
	$setBuildLogHistory,
	$updateBuildProgress,
	type IBuildCompleteEvent,
	type IBuildLiveProgress,
	type IBuildLogLine,
} from '@/entities/build-live.entity';
import { isDevMode } from '@/lib/is-dev-mode';
import { useUnit } from 'effector-react';
import { createElement, useEffect, useRef, type PropsWithChildren } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
export type socketHandleType = [string, (arg0: any, description: any, args: any[]) => void | Promise<void>];

export default function SocketProvider({ children }: PropsWithChildren) {
	const navigate = useNavigate();

	const [
		profile,
		resetAuth,
		setMyClients,
		setSelectedMyClient,
		setMyCodes,
		addMyClient,
		updateMyClient,
		selectedMyClient,
		addMyCode,
		setMyClientsSearchResult,
		selectedClient,
		setSelectedClient,
		updateClient,
		newClient,
		newBalance,
		updateBalancesByClientId,
		updateCardsByClientId,
		updatePhonesByClientId,
		updateBalance,
		setClientDetailCodes,
		setClientDetailBalances,
		setClientDetailMessages,
		addClientDetailCode,
		addClientDetailBalance,
		addClientDetailMessage,
		updateClientDetailBalance,
		setClientDetailActions,
		addClientDetailAction,
		updateClientDetailAction,
		setClientDetailApps,
		setOnlineClientsCount,
		updateBuildProgress,
		appendBuildLogs,
		setBuildLogHistory,
		setBuildComplete,
	] = useUnit([
		$profile,
		$resetAuth,
		$setMyClients,
		$setSelectedMyClient,
		$setMyCodes,
		$addMyClient,
		$updateMyClient,
		$selectedMyClient,
		$addMyCode,
		$setMyClientsSearchResult,
		$selectedClient,
		$setSelectedClient,
		$updateClient,
		$newClient,
		$newBalance,
		$updateBalancesByClientId,
		$updateCardsByClientId,
		$updatePhonesByClientId,
		$updateBalance,
		$setClientDetailCodes,
		$setClientDetailBalances,
		$setClientDetailMessages,
		$addClientDetailCode,
		$addClientDetailBalance,
		$addClientDetailMessage,
		$updateClientDetailBalance,
		$setClientDetailActions,
		$addClientDetailAction,
		$updateClientDetailAction,
		$setClientDetailApps,
		$setOnlineClientsCount,
		$updateBuildProgress,
		$appendBuildLogs,
		$setBuildLogHistory,
		$setBuildComplete,
	]);

	const selectedMyClientRef = useRef(selectedMyClient);
	const selectedClientRef = useRef(selectedClient);
	const hasConnectedOnce = useRef(false);

	useEffect(() => {
		hasConnectedOnce.current = false;
	}, [profile?.id]);

	useEffect(() => {
		selectedMyClientRef.current = selectedMyClient;
	}, [selectedMyClient]);

	useEffect(() => {
		selectedClientRef.current = selectedClient;
	}, [selectedClient]);

	useEffect(() => {
		if (isDevMode) return;
		if (!socket || !profile) return;
		if (!socket.connected && profile) {
			console.log('connect');
			socket.connect();
		}

		function handleSessionEnd() {
			resetAuth();
			socket.disconnect();
			navigate('/auth');
		}

		const handleError: socketHandleType = [
			SOCKET_EVENTS.error,
			(message: string) => {
				if (message === 'Сессия истекла') {
					toast.error(message);
					resetAuth();
					navigate('/auth');
					return;
				}
				toast.error(message);
			},
		];

		const handleSuccess: socketHandleType = [
			SOCKET_EVENTS.success,
			(message: string) => {
				toast.success(message);
			},
		];

		const handleConnect: socketHandleType = [
			SOCKET_EVENTS.connect,
			() => {
				if (!hasConnectedOnce.current) {
					toast.success('Соединение с сервером установлено', { duration: 1000 });
					hasConnectedOnce.current = true;
				}
			},
		];
		const handleDisconnect: socketHandleType = [
			SOCKET_EVENTS.disconnect,
			() => {
				handleSessionEnd();
			},
		];
		const handleSetProfile: socketHandleType = [SOCKET_EVENTS.setProfile, () => {}];

		const handleSetMyClients: socketHandleType = [
			SOCKET_EVENTS.setMyClients,
			(clients: IMyClient[]) => {
				setMyClients(clients);
			},
		];

		const handleSetMyClient: socketHandleType = [
			SOCKET_EVENTS.setMySelectedClient,
			(client: IMyClient) => {
				setSelectedMyClient(client);
			},
		];

		const handleSetMyCodes: socketHandleType = [
			SOCKET_EVENTS.setMyCodes,
			(codes: IMyCode[]) => {
				setMyCodes(codes);
			},
		];

		const handleNewCode: socketHandleType = [
			SOCKET_EVENTS.newCode,
			(code: IMyCode | undefined) => {
				if (!code) return;
				addMyCode(code);
				if (selectedClientRef.current?.id === code.clientId) {
					addClientDetailCode(code);
				}
			},
		];

		const handleNewClient: socketHandleType = [
			SOCKET_EVENTS.newClient,
			(client: any) => {
				addMyClient(client);
				newClient(client);
			},
		];

		const handleNewBalance: socketHandleType = [
			SOCKET_EVENTS.newBalance,
			(balance: IBalance) => {
				newBalance(balance);
				if (selectedClientRef.current?.id === balance.clientId) {
					addClientDetailBalance(balance);
				}
			},
		];

		const handleUpdateClient: socketHandleType = [
			SOCKET_EVENTS.updateClient,
			(client: any) => {
				if (!client) return;
				updateMyClient(client);
				updateClient(client);

				if (selectedMyClientRef.current?.id === client.id) {
					setSelectedMyClient({ ...selectedMyClientRef.current, ...client });
				}
				if (selectedClientRef.current?.id === client.id) {
					setSelectedClient({ ...selectedClientRef.current, ...client });
				}

				if (client.updateBalance) {
					updateBalancesByClientId({ clientId: client.id, ...{ id: undefined, ...client } });
				}

				if (client.takeStatus !== undefined || client.takenFrom !== undefined) {
					updateCardsByClientId({
						clientId: client.id,
						...(client.takeStatus !== undefined ? { takeStatus: client.takeStatus } : {}),
						...(client.takenFrom !== undefined ? { takenFrom: client.takenFrom } : {}),
					});
					updatePhonesByClientId({
						clientId: client.id,
						...(client.takeStatus !== undefined ? { takeStatus: client.takeStatus } : {}),
						...(client.takenFrom !== undefined ? { takenFrom: client.takenFrom } : {}),
					});
				}
			},
		];

		const handleUpdateBalance: socketHandleType = [
			SOCKET_EVENTS.updateBalance,
			(balance: IBalance) => {
				if (!balance) return;
				updateBalance(balance);
				updateClientDetailBalance(balance);
			},
		];

		const handleMyClientsSearchResult: socketHandleType = [
			SOCKET_EVENTS.myClientsSearchResult,
			(clients: IMyClient[]) => {
				setMyClientsSearchResult(clients);
			},
		];

		const handleSetSelectedClient: socketHandleType = [
			SOCKET_EVENTS.setSelectedClient,
			(client: IClient) => {
				setSelectedClient(client);
			},
		];

		const handleSetCodes: socketHandleType = [
			SOCKET_EVENTS.setCodes,
			(codes: IMyCode[]) => {
				setClientDetailCodes(codes);
			},
		];

		const handleSetBalances: socketHandleType = [
			SOCKET_EVENTS.setBalances,
			(balances: IBalance[]) => {
				setClientDetailBalances(balances);
			},
		];

		const handleSetMessages: socketHandleType = [
			SOCKET_EVENTS.setMessages,
			(messages: IClientMessage[]) => {
				setClientDetailMessages(messages);
			},
		];

		const handleSetActions: socketHandleType = [
			SOCKET_EVENTS.setActions,
			(actions: IClientAction[]) => {
				setClientDetailActions(actions);
			},
		];

		const handleSetClientApps: socketHandleType = [
			SOCKET_EVENTS.setClientApps,
			(payload: { apps: IInstalledApp[]; count: number }) => {
				setClientDetailApps(payload.apps);
			},
		];

		const handleNewAction: socketHandleType = [
			SOCKET_EVENTS.newAction,
			(action: IClientAction | undefined) => {
				if (!action) return;
				if (selectedClientRef.current?.id === action.clientId) {
					addClientDetailAction(action);
				}
			},
		];

		const handleUpdateAction: socketHandleType = [
			SOCKET_EVENTS.updateAction,
			(update: Partial<IClientAction> & { id: number; clientId: string }) => {
				if (!update?.id) return;
				if (selectedClientRef.current?.id === update.clientId) {
					updateClientDetailAction(update);
				}
			},
		];

		const handleNewMessage: socketHandleType = [
			SOCKET_EVENTS.newMessage,
			(message: IClientMessage | undefined) => {
				if (!message) return;
				if (selectedClientRef.current?.id === message.clientId) {
					addClientDetailMessage(message);
				}
			},
		];

		const handleOnlineCount: socketHandleType = [
			SOCKET_EVENTS.onlineCount,
			(count: number) => {
				if (profile?.role === 'carder' || profile?.role === 'admin') {
					setOnlineClientsCount(count);
				}
			},
		];

		const handleBuildProgress: socketHandleType = [
			SOCKET_EVENTS.buildProgress,
			(payload: IBuildLiveProgress) => {
				if (!payload?.buildId) return;
				updateBuildProgress(payload);
			},
		];

		const handleBuildLogLine: socketHandleType = [
			SOCKET_EVENTS.buildLogLine,
			(payload: { buildId?: string; lines?: IBuildLogLine[] }) => {
				if (!payload?.buildId || !payload.lines?.length) return;
				if (profile?.role !== 'admin') return;
				appendBuildLogs({ buildId: payload.buildId, lines: payload.lines });
			},
		];

		const handleBuildLogHistory: socketHandleType = [
			SOCKET_EVENTS.buildLogHistory,
			(payload: { buildId?: string; lines?: IBuildLogLine[] }) => {
				if (!payload?.buildId || !payload.lines) return;
				if (profile?.role !== 'admin') return;
				setBuildLogHistory({ buildId: payload.buildId, lines: payload.lines });
			},
		];

		const handleBuildComplete: socketHandleType = [
			SOCKET_EVENTS.buildComplete,
			(payload: IBuildCompleteEvent) => {
				if (!payload?.buildId) return;
				setBuildComplete(payload);
				if (payload.success) {
					toast.success('Сборка завершена');
					return;
				}
				if (payload.errorMessage === 'Сборка отменена') {
					toast.info('Сборка отменена');
					return;
				}
				toast.error(payload.errorMessage ?? 'Сборка завершилась с ошибкой');
			},
		];

		const handlers = [
			handleError,
			handleSuccess,
			handleConnect,
			handleDisconnect,
			handleSetProfile,
			handleSetMyClients,
			handleSetMyClient,
			handleSetMyCodes,
			handleNewClient,
			handleUpdateClient,
			handleNewCode,
			handleMyClientsSearchResult,
			handleNewBalance,
			handleUpdateBalance,
			handleSetSelectedClient,
			handleSetCodes,
			handleSetBalances,
			handleSetMessages,
			handleSetActions,
			handleSetClientApps,
			handleNewAction,
			handleUpdateAction,
			handleNewMessage,
			handleOnlineCount,
			handleBuildProgress,
			handleBuildLogLine,
			handleBuildLogHistory,
			handleBuildComplete,
		];
		handlers.forEach(h => socket.on(...h));

		return () => {
			handlers.forEach(h => socket.off(...h));
		};
	}, [profile]);

	return createElement('div', { className: 'h-full w-full min-h-0 flex flex-col overflow-hidden' }, children);
}
